"use client"

import { useState, useEffect } from "react"
import "../css/SendMail.css" // Assuming this CSS file exists
import { BASE_URL } from "../config" // Assuming this config file exists
import { getAuthHeaders } from "../services/useAuth" // Assuming this service exists
import { Paperclip, FileText, X } from "lucide-react"
import CustomButton from "./CustomButton"

const MessageNews = () => {
  const [messages, setMessages] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [viewMode, setViewMode] = useState("list")
  const [currentPage, setCurrentPage] = useState(1)
  const [employees, setEmployees] = useState([])
  const [toRecipients, setToRecipients] = useState([])
  const [ccRecipients, setCcRecipients] = useState([])
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [error, setError] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [messagesPerPage, setMessagesPerPage] = useState(10)
  // const loggedInEmployeeId = 340

  // Function to fetch inbox messages
  const fetchInbox = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${BASE_URL}/api/inbox-mail`, {
        headers: getAuthHeaders(),
      })
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      const formattedMessages = data.map((message) => {
        const rawDate = new Date(message.Date)
        const day = rawDate.getDate()
        const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][
          rawDate.getMonth()
        ]
        const year = rawDate.getFullYear()
        const hours = rawDate.getHours().toString().padStart(2, "0")
        const minutes = rawDate.getMinutes().toString().padStart(2, "0")
        const formattedDate = `${day} ${month} ${year} ${hours}:${minutes}`
        return {
          id: message.id,
          ref: message.ref,
          name: message.From,
          date: formattedDate,
          type: message.Type,
          action: message.Action,
          from: message.From,
          subject: message.Subject,
          contentDate: message.Date,
          hasAttachment: message.Attachment !== null && message.Attachment !== undefined,
          isSelected: false,
          body: message.body,
        }
      })
      setMessages(formattedMessages)
    } catch (error) {
      console.error("Could not fetch inbox messages:", error)
      setError("Could not fetch inbox messages. Please check your network connection and try again.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchInbox()
  }, [])

  useEffect(() => {
    if (viewMode === "compose") {
      fetch(`${BASE_URL}/api/employees`, {
        headers: getAuthHeaders(),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          setEmployees(data)
          setError(null)
        })
        .catch((error) => {
          console.error("Could not fetch employees:", error)
          setEmployees([])
          setError("Could not fetch employees. Please try again later.")
        })
    }
  }, [viewMode])

const handleSidebarClick = (item) => {
  if (item === "Compose Mail") {
    setViewMode("compose")
  } else if (item === "Inbox") {
    setViewMode("list")
    fetchInbox()
  }
}


  const handleSelectAllMessages = (e) => {
    const isChecked = e.target.checked
    setSelectAll(isChecked)
    setMessages(messages.map((msg) => ({ ...msg, isSelected: isChecked })))
  }

  const handleSelectMessage = (id, e) => {
    e.stopPropagation()
    setMessages(messages.map((msg) => (msg.id === id ? { ...msg, isSelected: !msg.isSelected } : msg)))
  }

  const handleRowClick = (message) => {
    setSelectedMessage(message)
    setViewMode("email")
  }
const handleDeleteSelectedMessages = async () => {
  // Extract numeric `id` values for selected messages
  const selectedIds = messages
    .filter(msg => msg.id)  // Filter selected messages
    .map(msg => msg.id);            // Use the numeric `id` field (e.g., 50, 49)

  try {
    const response = await fetch(`${BASE_URL}/api/mails/deactivate`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(), // Include auth headers
      },
      body: JSON.stringify({
        mail_ids: selectedIds,  // Pass numeric `id`s (e.g., [50, 49])
        is_active: false,       // Set to false to deactivate the emails
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Update UI after deactivation
    setMessages(messages.filter(msg => !msg.isSelected));  // Remove deactivated messages from the UI
    setSelectAll(false);
    if (selectedMessage && selectedIds.includes(selectedMessage.id)) {
      setSelectedMessage(null);
    }
    setCurrentPage(1);
    console.log("Messages deactivated successfully.");
  } catch (error) {
    console.error("Error deactivating messages:", error);
    setError("Failed to deactivate messages. Please try again.");
  }
};



  const handleBackToList = () => {
    setViewMode("list")
    fetchInbox() // Re-fetch inbox when returning to list view
  }

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= Math.ceil(messages.length / messagesPerPage)) {
      setCurrentPage(pageNumber)
    }
  }

  const handleToChange = (e) => {
    const selected = Array.from(e.target.options)
      .filter((option) => option.selected)
      .map((option) => option.value)
    setToRecipients(selected)
  }

  const handleCcChange = (e) => {
    const selected = Array.from(e.target.options)
      .filter((option) => option.selected)
      .map((option) => option.value)
    setCcRecipients(selected)
  }

  const handleSubjectChange = (e) => {
    setSubject(e.target.value)
  }

  const handleBodyChange = (e) => {
    setBody(e.target.value)
  }
const handleSendMail = async () => {
  if (!toRecipients.length) {
    setError("Please select at least one recipient.");
    return;
  }
  if (!subject.trim()) {
    setError("Please enter a subject.");
    return;
  }
  if (!body.trim()) {
    setError("Please enter a message body.");
    return;
  }
  setError(null);

  // Convert email recipients to employee IDs
  const toEmployeeIds = toRecipients.map(email => {
    const employee = employees.find(emp => emp.email === email);
    return employee ? employee.id : null;
  }).filter(id => id !== null);

  if (toEmployeeIds.length === 0) {
    setError("Please select valid recipients.");
    return;
  }

  const formData = new URLSearchParams();
  formData.append('to_employee_ids', toEmployeeIds.join(","));
  formData.append('from_employee_id', '405'); 
  formData.append('subject', subject);
  formData.append('body', body);
  if (selectedFile) {
    formData.append('attachment', selectedFile);
  }
  formData.append('attachment', selectedFile);
  try {
    const response = await fetch(`${BASE_URL}/api/send-mail`, {
      method: "POST",
      headers: {
        ...getAuthHeaders(),
      },
      body: formData.toString(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Failed to send email: ${response.status}`);
    }

    const sentMail = await response.json();
    console.log("Email sent successfully:", sentMail);

    // Reset state
    setToRecipients([]);
    setCcRecipients([]);
    setSubject("");
    setBody("");
    setSelectedFile(null);
    setViewMode("list");
    await fetchInbox();
  } catch (error) {
    console.error("Error sending email:", error);
    setError(error.message || "Failed to send email. Please try again.");
  }
};

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // if (file.size > 2097152) {
      //   // setError("File size exceeds 2MB limit.")
      //   e.target.value = ""
      //   setSelectedFile(null)
      //   return
      // }
      setSelectedFile(file)
      setError(null)
    } else {
      setSelectedFile(null)
    }
  }

  const sidebarItems = ["Inbox", "Compose Mail"]

  const indexOfLastMessage = currentPage * messagesPerPage
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage
  const currentMessages = messages.slice(indexOfFirstMessage, indexOfLastMessage)
  const totalPages = Math.ceil(messages.length / messagesPerPage)

  return (
    <div className="app-container">
      <div className="main-content-container">
        <div className="layout-container">
          {/* Sidebar Navigation */}
          <div className="sidebar">
            <ul className="sidebar-menu">
              {sidebarItems.map((item, index) => (
                <li
                  key={index}
                  className={`sidebar-item ${viewMode === "list" && item === "Inbox" ? "active" : viewMode === "compose" && item === "Compose Mail" ? "active" : ""}`}
                  onClick={() => handleSidebarClick(item)}
                >
                  <a
                    href="#"
                    className={
                      viewMode === "list" && item === "Inbox"
                        ? "active"
                        : viewMode === "compose" && item === "Compose Mail"
                          ? "active"
                          : ""
                    }
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          {/* Main Content Area */}
          <div className="content-area">
            {viewMode === "list" ? (
              <div className="message-list-container">
                <div className="mb-4">
                  <h1 className="text-lg font-semibold text-gray-800 border-b pb-2">Message's Inbox</h1>
                </div>
                <section className="composition-section">
                  {loading ? (
                    <p>Loading messages...</p>
                  ) : error ? (
                    <p className="error-message">{error}</p>
                  ) : (
                    <div className="table-container">
                      <table className="messages-table">
                        <thead className="text-white">
                          <tr>
                            <th>S.NO</th>
                            <th>Ref</th>
                            <th>Type</th>
                            <th>Action</th>
                            <th>From</th>
                            <th>Subject</th>
                            <th>Date</th>
                            <th>Attachment</th>
                            <th>
                              <div className="flex flex-col items-center">
                                <button
                                  className="delete-all-btn mb-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs"
                                  onClick={handleDeleteSelectedMessages}
                                  disabled={messages.length === 0 || !messages.some((msg) => msg.isSelected)}
                                >
                                  Delete
                                </button>
                                <input
                                  type="checkbox"
                                  checked={selectAll}
                                  onChange={handleSelectAllMessages}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentMessages.map((message, index) => (
                            <tr
                              key={message.id}
                              className={message.isSelected ? "selected-row" : ""}
                              onClick={() => handleRowClick(message)}
                            >
                              <td>{indexOfFirstMessage + index + 1}</td>
                              <td className="ref-cell">{message.ref}</td>
                              <td className={`type-cell ${message.type ? message.type.toLowerCase() : ""}`}>
                                {message.type}
                              </td>
                              <td>{message.action}</td>
                              <td className="from-cell">{message.from}</td>
                              <td className="subject-cell">{message.subject}</td>
                              <td>{message.date}</td>
                              <td>{message.hasAttachment ? <span className="attachment-icon">📎</span> : null}</td>
                              <td className="text-center align-middle">
                                <input
                                  type="checkbox"
                                  checked={message.isSelected}
                                  onChange={(e) => handleSelectMessage(message.id, e)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                 {!loading && !error && (
  <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-gray-100 border-t border-gray-300 mt-4">
    <div className="flex items-center mb-2 sm:mb-0">
      <label htmlFor="itemsPerPage" className="mr-2 font-medium text-sm text-gray-700">
        View
      </label>
      <select
        id="itemsPerPage"
        className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
        value={messagesPerPage}
        onChange={(e) => setMessagesPerPage(Number(e.target.value))}
      >
        <option value="10">10</option>
        <option value="20">20</option>
        <option value="30">30</option>
        <option value="50">50</option>
      </select>
    </div>
    <div className="flex items-center space-x-3">
      <span className="text-sm text-gray-600 font-medium">
        {indexOfFirstMessage + 1}-{Math.min(indexOfLastMessage, messages.length)} of {messages.length}
      </span>
      <button
        disabled={currentPage === 1}
        onClick={() => paginate(currentPage - 1)}
        className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        ←
      </button>
      <button
        disabled={currentPage === totalPages}
        onClick={() => paginate(currentPage + 1)}
        className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        →
      </button>
    </div>
  </div>
)}
                </section>
              </div>
            ) : viewMode === "email" && selectedMessage ? (
              <div className="email-view-container">
                <div className="email-view-header">
                  <button className="back-button" onClick={handleBackToList}>
                    <span className="material-icons">Back to Inbox</span>
                  </button>
                </div>
                <div className="email-content">
                  <div className="email-details-container">
                    <div className="detail-row">
                      <span className="detail-label">From:</span>
                      <span className="detail-value">{selectedMessage?.from}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">To:</span>
                      <span className="detail-value">
                        {selectedMessage.toEmployees ? selectedMessage.toEmployees.join(", ") : "You"}
                      </span>
                    </div>
                    {selectedMessage.ccEmployees && selectedMessage.ccEmployees.length > 0 && (
                      <div className="detail-row">
                        <span className="detail-label">CC:</span>
                        <span className="detail-value">{selectedMessage.ccEmployees.join(", ")}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">Ref No:</span>
                      <span className="detail-value">{selectedMessage?.id}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Subject:</span>
                      <span className="detail-value">{selectedMessage?.subject}</span>
                    </div>
                  </div>
                  <div className="email-message-container">
                    <div className="email-body" dangerouslySetInnerHTML={{ __html: selectedMessage?.body }} />
                  </div>
                  <div className="email-actions">
                    <button className="email-action-button-print-button" onClick={() => window.print()}>
                      Print
                    </button>
                    <button className="email-action-button-reply-button" onClick={() => setViewMode("compose")}>
                      Reply
                    </button>
                    <button className="email-action-button-forward-button" onClick={() => setViewMode("compose")}>
                      Forward
                    </button>
                  </div>
                </div>
              </div>
            ) : viewMode === "compose" ? (
              <div className="compose-mail-container bg-white p-6 rounded-lg shadow-">
                <div className="mb-6">
                  <h2 className="text-2xl font-semibold text-gray-800 border-b pb-2">Compose New Mail</h2>
                  {error && <p className="text-red-600 mt-2 text-sm">{error}</p>}
                </div>
                <div className="recipients-row mb-6 space-y-4">
                  {/* To Employees Section */}
                  <div className="form-group to-group">
                    <div className="employee-section-header flex justify-between items-center">
                      <label htmlFor="to" className="text-sm font-semibold text-gray-800">
                        To Employees ({employees.length})
                      </label>
                      <input
                        type="checkbox"
                        checked={toRecipients.length === employees.length && employees.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setToRecipients(employees.map((emp) => emp.email))
                          } else {
                            setToRecipients([])
                          }
                        }}
                        disabled={employees.length === 0}
                        className="form-checkbox h-5 w-5 text-blue-500"
                      />
                    </div>
                    <div className="employee-container space-y-2 max-h-60 overflow-y-auto mt-2">
                      {employees.map((employee) => (
                        <div key={`to-${employee.id}`} className="employee-item flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`to-${employee.id}`}
                            value={employee.email}
                            checked={toRecipients.includes(employee.email)}
                            onChange={(e) => {
                              const value = e.target.value
                              setToRecipients((prev) =>
                                e.target.checked ? [...prev, value] : prev.filter((email) => email !== value),
                              )
                            }}
                            className="form-checkbox h-4 w-4 text-blue-500"
                          />
                          <label htmlFor={`to-${employee.id}`} className="text-sm text-gray-700 truncate max-w-xs">
                            {employee.first_name} {employee.last_name} | {employee.designation} |{" "}
                            {employee.headquarter_name}| {employee.division} | {employee.state_name} ({employee.email})
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* CC Employees Section */}
                  <div className="form-group cc-group">
                    <div className="employee-section-header flex justify-between items-center">
                      <label htmlFor="cc" className="text-sm font-semibold text-gray-800">
                        CC Employees ({employees.length})
                      </label>
                      <input
                        type="checkbox"
                        checked={ccRecipients.length === employees.length && employees.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCcRecipients(employees.map((emp) => emp.email))
                          } else {
                            setCcRecipients([])
                          }
                        }}
                        disabled={employees.length === 0}
                        className="form-checkbox h-5 w-5 text-blue-500"
                      />
                    </div>
                    <div className="employee-container space-y-2 max-h-60 overflow-y-auto mt-2">
                      {employees.map((employee) => (
                        <div key={`cc-${employee.id}`} className="employee-item flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`cc-${employee.id}`}
                            value={employee.email}
                            checked={ccRecipients.includes(employee.email)}
                            onChange={(e) => {
                              const value = e.target.value
                              setCcRecipients((prev) =>
                                e.target.checked ? [...prev, value] : prev.filter((email) => email !== value),
                              )
                            }}
                            className="form-checkbox h-4 w-4 text-blue-500"
                          />
                          <label htmlFor={`cc-${employee.id}`} className="text-sm text-gray-700 truncate max-w-xs">
                            {employee.first_name} {employee.last_name} | {employee.designation} |{" "}
                            {employee.headquarter_name}| {employee.division} | {employee.state_name} ({employee.email})
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Subject and Body Section */}
                <div className="form-group mb-6">
                  <label htmlFor="subject" className="text-sm font-semibold text-gray-800">
                    Subject:
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={subject}
                    onChange={handleSubjectChange}
                    className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="form-group mb-6">
                  <label htmlFor="body" className="text-sm font-semibold text-gray-800">
                    Body:
                  </label>
                  <textarea
                    id="body"
                    value={body}
                    onChange={handleBodyChange}
                    className="w-full mt-2 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-40"
                  ></textarea>
                </div>
                {/* Footer with Buttons and Attachment */}
                <div className="send-form-footer flex justify-between items-center">
                  <CustomButton onClick={handleSendMail} className=" whitespace-nowrap" variant="save">
                    Send
                  </CustomButton>
                  <div className="flex items-center gap-4 mt-4">
                    <label
                      htmlFor="attachment"
                      className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <Paperclip className="w-4 h-4" />
                      Choose File
                    </label>
                    <input type="file" id="attachment" className="hidden" onChange={handleFileChange} />
                    {selectedFile && (
                      <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                        <FileText className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-800">{selectedFile.name}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedFile(null)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    {!selectedFile && <span className="text-gray-500 text-xs">No file chosen</span>}
                  </div>
                </div>
                {/* File Upload Guidelines */}
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="text-xs font-semibold text-blue-800 mb-1">File Upload Guidelines:</h4>
                  <ul className="text-xs text-blue-700 list-disc ml-4">
                    <li>Supported formats: xls, xlsx, pdf, doc, docx, txt, png, jpg, jpeg, gif, zip, rar</li>
                    <li>Maximum file size: 2MB</li>
                    <li>For multiple files, use WinRAR or WinZip to create a single compressed file</li>
                  </ul>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MessageNews
